import request from '@capturi/request'
import chunk from 'lodash/chunk'
import debounce from 'lodash/debounce'

import type { ErrorAggregator } from './aggregator'

export type FlushOptions = {
  shouldLogToService: boolean
  isOnline?: () => boolean
}

type IngestErrorsResponse = {
  success: boolean
  processed: number
}

// Keep in sync with MAX_ERRORS_PER_REQUEST in the frontend-error-ingest
// service. Must stay <= the server limit so a batch is never rejected.
const MAX_ERRORS_PER_REQUEST = 50

/**
 * Prepare events to send to error service.
 */
const prepareEventsToSend = (aggregator: ErrorAggregator) => {
  const all = aggregator.getAll()
  if (all.length === 0) {
    return { eventsToSend: [], requestBody: { errors: [] } }
  }

  const eventsToSend = all.map(([signature, agg]) => {
    const { errorData, count } = agg
    const baseMessage = errorData.message || 'Unknown error'
    const finalMessage =
      count > 1 ? `${baseMessage} [Aggregated x${count}]` : baseMessage

    return {
      signature,
      payload: {
        timestamp: new Date(),
        level: 'error' as const,
        message: finalMessage,
        context: {
          url: errorData.url || '',
          userAgent: errorData.userAgent || '',
          stack: errorData.stack,
          componentStack: errorData.componentStack,
          occurrences: count,
          ...errorData,
        },
      },
    }
  })

  // Build request body in the format expected by the service
  const requestBody = {
    errors: eventsToSend.map((ev) => ev.payload),
  }

  return { eventsToSend, requestBody }
}

export const createLogFlusher = (
  aggregator: ErrorAggregator,
  opts: FlushOptions,
) => {
  /**
   * The actual flush method we call after the debounce timer.
   */
  const flush = async () => {
    if (!opts.shouldLogToService) {
      // dev mode: just log to console
      const { eventsToSend, requestBody } = prepareEventsToSend(aggregator)
      if (eventsToSend.length > 0) {
        // oxlint-disable-next-line no-console -- Log in dev mode only
        console.log(
          '[Would send to error service] Aggregated errors:',
          requestBody,
        )
        aggregator.clear()
      }
      return
    }

    // If offline, clear old aggregated errors to prevent memory growth
    if (opts.isOnline && !opts.isOnline()) {
      // Clear aggregated errors older than 5 minutes to prevent unbounded memory growth
      aggregator.clearOldErrors(5 * 60 * 1000) // 5 minutes in milliseconds
      return
    }

    const { eventsToSend, requestBody } = prepareEventsToSend(aggregator)
    if (eventsToSend.length === 0) return
    aggregator.clear()
    try {
      // Chunk so we never exceed the server's per-request error limit.
      for (const errors of chunk(requestBody.errors, MAX_ERRORS_PER_REQUEST)) {
        await request.post<IngestErrorsResponse>('errors/ingest', {
          json: { errors },
          retry: 3, // Use ky's built-in retry mechanism with 3 retries
        })
      }
    } catch (err) {
      console.error('Failed to send logs to error service after retries:', err)
    }
  }
  return { flush }
}

export type LogFlusher = ReturnType<typeof createLogFlusher>

/**
 * Create a debounced function that calls .flush() on the flusher,
 * with given wait/maxWait.
 */
export const createDebouncedFlush = (opts: {
  flusher: LogFlusher
  wait: number
  maxWait: number
}) => {
  const debounced = debounce(
    () => {
      opts.flusher.flush()
    },
    opts.wait,
    {
      leading: false,
      trailing: true,
      maxWait: opts.maxWait,
    },
  )
  return debounced
}
